import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getAllBlogs, getBlogById } from "../../controllers/homeController";
import { slugify } from "../../utils/slugify";

// ——— utils ——————————————————————————————————————————
function formatDate(iso) {
  try {
    const d = new Date(iso);
    return isNaN(d)
      ? ""
      : d.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
  } catch {
    return "";
  }
}

// ——— skeleton ——————————————————————————————————————
const Loading = () => (
  <main className="bg-background text-text py-14 sm:py-16">
    <div className="w-full mx-auto max-w-[860px] px-5 sm:px-6">
      <div className="h-3 w-28 bg-muted/30 rounded mb-3 animate-pulse" />
      <div className="h-10 w-3/4 bg-muted/30 rounded mb-4 animate-pulse" />
      <div className="h-5 w-2/3 bg-muted/20 rounded mb-6 animate-pulse" />
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-full bg-muted/20 animate-pulse" />
        <div className="h-4 w-40 bg-muted/20 rounded animate-pulse" />
      </div>
      <div className="h-[320px] w-full bg-muted/20 rounded-2xl mb-8 animate-pulse" />
      <div className="space-y-3">
        <div className="h-4 w-full bg-muted/20 rounded animate-pulse" />
        <div className="h-4 w-5/6 bg-muted/20 rounded animate-pulse" />
        <div className="h-4 w-4/6 bg-muted/20 rounded animate-pulse" />
      </div>
    </div>
  </main>
);

// ——— page ———————————————————————————————————————————
export default function BlogsDetails() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const normalizedSlug = decodeURIComponent(String(slug || ""))
        .toLowerCase()
        .trim();

      const list = await getAllBlogs();
      const match =
        list.find(
          (b) =>
            String(b.slug || "")
              .toLowerCase()
              .trim() === normalizedSlug
        ) || list.find((b) => slugify(b.title || "") === normalizedSlug);

      if (!alive) return;

      if (!match) {
        setBlog(null);
        setLoading(false);
        return;
      }

      setBlog(match); // optimistic paint
      const full = await getBlogById(match.id);
      if (!alive) return;

      setBlog(full || match);
      setLoading(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    })();
    return () => {
      alive = false;
    };
  }, [slug]);

  if (loading) return <Loading />;

  if (!blog) {
    return (
      <main className="bg-background text-black dark:text-textDark py-20">
        <div className="w-full mx-auto max-w-[860px] px-5 sm:px-6">
          <Link
            to="/blogs"
            className="text-sm text-muted underline hover:text-primary"
          >
            ← Back to Blog
          </Link>
          <h1 className="mt-4 text-3xl font-extrabold text-text">
            Article not found
          </h1>
          <p className="mt-2 text-muted">
            This article doesn’t exist or was removed.
          </p>
        </div>
      </main>
    );
  }

  const {
    title,
    author = "Mr Reel Team",
    authorRole = "Product Designer", // optional; shows like the mock
    imageUrl,
    publishedAt,
    excerpt,
    content,
  } = blog;

  return (
    <main className="bg-background dark:bg-darkbg text-black dark:text-textDark py-10 sm:py-16">
      <article className="w-full mx-auto max-w-[860px] px-5 sm:px-6 text-left">
        {/* meta */}
        <div className="text-xs text-muted">
          <time dateTime={publishedAt}>{formatDate(publishedAt)}</time>
          <span className="mx-2">•</span>
          <span>Articles</span>
        </div>

        {/* title */}
        <h1 className="mt-2 text-[32px] leading-tight sm:text-5xl font-extrabold tracking-tight">
          {title}
        </h1>

        {/* dek */}
        {excerpt && (
          <p className="mt-3 text-[15px] sm:text-lg text-muted">{excerpt}</p>
        )}

        {/* author row */}
        <div className="mt-6 flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary/50 to-primary/10 grid place-items-center">
            <span className="text-[11px] font-bold">
              {String(author).slice(0, 1).toUpperCase()}
            </span>
          </div>
          <div className="leading-tight">
            <div className="text-sm font-medium">{author}</div>
            <div className="text-xs text-muted">{authorRole || "Author"}</div>
          </div>
        </div>

        {/* cover */}
        <div className="mt-6 rounded-2xl overflow-hidden bg-muted/10">
          <div className="relative w-full aspect-[16/9]">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={title}
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
                decoding="async"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                {/* centered placeholder icon + faint radial guides (like the mock) */}
                <svg
                  width="140"
                  height="140"
                  viewBox="0 0 140 140"
                  className="opacity-50"
                >
                  <circle
                    cx="70"
                    cy="70"
                    r="60"
                    fill="none"
                    stroke="currentColor"
                    strokeDasharray="2 6"
                  />
                  <line
                    x1="70"
                    y1="10"
                    x2="70"
                    y2="130"
                    stroke="currentColor"
                    strokeDasharray="2 6"
                  />
                  <line
                    x1="10"
                    y1="70"
                    x2="130"
                    y2="70"
                    stroke="currentColor"
                    strokeDasharray="2 6"
                  />
                  <rect
                    x="55"
                    y="55"
                    width="30"
                    height="30"
                    rx="6"
                    fill="none"
                    stroke="currentColor"
                  />
                  <path d="M65 73l4 5 6-8 8 11H57z" fill="currentColor" />
                </svg>
              </div>
            )}
          </div>
        </div>

        {/* body */}
        <div
          className={[
            "article-content mt-8 max-w-none",
            "prose prose-neutral prose-lg dark:prose-invert",
            "prose-headings:font-extrabold prose-h2:mt-10 prose-h2:mb-3",
            "prose-p:leading-7 sm:prose-p:leading-8",
            "prose-p:text-left prose-h1:text-left prose-h2:text-left prose-h3:text-left",
            "prose-ul:text-left prose-ol:text-left prose-li:text-left prose-blockquote:text-left",
            "prose-a:underline hover:prose-a:text-primary",
            "prose-img:rounded-xl prose-blockquote:border-l-4 prose-blockquote:border-primary/40",
          ].join(" ")}
          dangerouslySetInnerHTML={{ __html: content }}
        />

        {/* footer */}
        <footer className="mt-10 pt-6 border-t border-white/10 text-sm text-muted flex items-center justify-between gap-4 flex-wrap">
          <span>Written by {author}</span>
          <a
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
              title
            )}&url=${encodeURIComponent(window.location.href)}`}
            className="underline hover:text-primary"
            target="_blank"
            rel="noreferrer"
          >
            Share
          </a>
        </footer>

        {/* back link */}
        <div className="mt-6">
          <Link
            to="/blogs"
            className="text-sm text-muted underline hover:text-primary"
          >
            ← Back to Blog
          </Link>
        </div>
      </article>
    </main>
  );
}
